import {
  performReorderMainSections,
  performReorderSectionQuestions,
  performReorderSubSections,
  resolveMainSectionReorderContext,
  resolveQuestionReorderContext,
  resolveSubSectionReorderContext,
} from './create_sections_reorder_operation_helpers.js';
import {
  buildMainSectionReorderPayload,
  isValidReorderRange,
  moveItemByIndex,
  resolveFormMainSectionId,
  resolveSubSectionId,
} from './create_sections_reorder_value_helpers.js';

async function withReorderLoader(set_loading, ctx, message, action) {
  set_loading(true);
  const loader = await ctx.show_loader(message);
  try {
    await action();
  } finally {
    await ctx.close_loader(loader);
    set_loading(false);
  }
}

function validateMainSectionReorderContext(ctx, reorder_context) {
  if (!reorder_context.form_id) {
    ctx.show_error('Please select a valid form section first.');
    return false;
  }
  return reorder_context.has_valid_range;
}

function resolveMainSectionReorderPayload(ctx, source_index, target_index) {
  const reorder_context = resolveMainSectionReorderContext(
    ctx,
    source_index,
    target_index,
    isValidReorderRange
  );
  if (!validateMainSectionReorderContext(ctx, reorder_context)) return null;

  const moved_result = moveItemByIndex(reorder_context.ordered_sections, source_index, target_index);
  if (!moved_result) return null;

  return buildMainSectionReorderPayload(
    reorder_context.form_id,
    moved_result.reordered_items
  );
}

export async function reorderMainSectionsByIndex(ctx, source_index, target_index) {
  const payload = resolveMainSectionReorderPayload(ctx, source_index, target_index);
  if (!payload) return;

  await withReorderLoader((is_loading) => {
    ctx.reordering_main_sections = is_loading;
  }, ctx, 'Reordering main sections...', async () => {
    await performReorderMainSections(ctx, payload);
  });
}

function hasValidSubSectionReorderContext(reorder_context) {
  return [
    reorder_context.form_main_section_id,
    reorder_context.moved_result,
    reorder_context.form_section_id,
  ].every(Boolean);
}

function resolveValidSubSectionReorderContext(ctx, main_section, source_index, target_index) {
  const reorder_context = resolveSubSectionReorderContext(
    main_section,
    source_index,
    target_index,
    isValidReorderRange,
    moveItemByIndex,
    resolveFormMainSectionId,
    resolveSubSectionId
  );
  if (hasValidSubSectionReorderContext(reorder_context)) return reorder_context;

  ctx.show_error('Please select a valid sub section first.');
  return null;
}

export async function reorderSubSectionsByIndex(ctx, main_section, source_index, target_index) {
  const reorder_context = resolveValidSubSectionReorderContext(
    ctx,
    main_section,
    source_index,
    target_index
  );
  if (!reorder_context) return;

  await withReorderLoader((is_loading) => {
    ctx.reordering_sub_sections = is_loading;
  }, ctx, 'Reordering sub sections...', async () => {
    await performReorderSubSections(ctx, reorder_context, target_index);
  });
}

function validateQuestionReorderContext(ctx, reorder_context) {
  const has_moved_question = Boolean(reorder_context.moved_result);
  if (has_moved_question && reorder_context.section_question_id) return true;
  if (has_moved_question) {
    ctx.show_error('Could not find the selected question mapping.');
  }
  return false;
}

function resolveValidQuestionReorderContext(ctx, section, source_index, target_index) {
  const reorder_context = resolveQuestionReorderContext(
    section,
    source_index,
    target_index,
    isValidReorderRange,
    moveItemByIndex
  );
  return validateQuestionReorderContext(ctx, reorder_context) ? reorder_context : null;
}

export async function reorderSectionQuestions(ctx, section, source_index, target_index) {
  const reorder_context = resolveValidQuestionReorderContext(
    ctx,
    section,
    source_index,
    target_index
  );
  if (!reorder_context) return;

  await withReorderLoader((is_loading) => {
    ctx.reordering_section_questions = is_loading;
  }, ctx, 'Reordering questions...', async () => {
    await performReorderSectionQuestions(ctx, section, reorder_context, target_index);
  });
}
